/* eslint-disable react/prop-types */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import AnimatedNumber from './AnimatedNumber';
import StatsTagBreakdown from './StatsTagBreakdown';
import { MONTH_VIEW_WEEKS, MONTH_VIEW_WINDOW_DAYS } from '../../utils/monthViewWindow';
import getProgramDayTotals from '../../utils/programDayTotals';

const Wrapper = styled('div')`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  background: white;
  overflow: hidden;
`;

/** Scrolls independently so the title and toggle above it stay fixed at the top. */
const ScrollBody = styled('div')`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
`;

/** Segmented control for the week-view Tag Breakdown Day/Week toggle. Grey track, active option is
 * a white thumb with a subtle shadow; spans the full width. */
const SegmentedControl = styled('div')`
  display: flex;
  width: 100%;
  padding: 2px;
  gap: 4px;
  border-radius: 4px;
  background: #EEEEEE;
`;

const Segment = styled('div')`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 250ms ease-in-out;
  background: ${(props) => (props.isSelected ? 'white' : 'transparent')};
  box-shadow: ${(props) => (props.isSelected
    ? '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)'
    : 'none')};
`;

/** Underlined text-tab bar for the top Volume/Tag switcher (week & month views). The active tab
 * gets a blue underline that sits on the grey rail beneath both tabs. */
const TabBar = styled('div')`
  display: flex;
  width: 100%;
  gap: 24px;
  border-bottom: 1px solid #EEEEEE;
`;

const TabItem = styled('div')`
  padding-bottom: 8px;
  margin-bottom: -1px;
  cursor: pointer;
  border-bottom: 2px solid ${(props) => (props.isSelected ? '#4A90E2' : 'transparent')};
`;

const DateBlock = styled('div')`
  display: flex;
  justify-content: ${(props) => (props.isNav ? 'space-between' : 'center')};
  align-items: center;
  width: 100%;
  padding-top: ${(props) => (props.padTop ? '8px' : '0')};
  border-radius: 8px;
  border: 1px solid ${(props) => (props.isFocus || props.borderless ? 'transparent' : '#444444')};
  background: ${(props) => {
    if (props.borderless) return 'transparent';
    if (props.isFocus) return '#D6D9DE';
    if (props.isMonth) return '#F5F5F5';
    return 'white';
  }};
  text-align: center;
`;

/** Back/forward arrow for the Daily-mode day nav. Disabled at the ends of the displayed week. */
const NavArrow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
  :hover {
    opacity: ${(props) => (props.disabled ? 0.25 : 0.6)};
  }
`;

/** One week's summary in the month-view breakdown: the date-range header plus a 3-column row of
 * Total Sets / Total Reps / Estimated Volume. */
const WeekSummary = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const WeekStatsRow = styled('div')`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const TileRow = styled('div')`
  display: flex;
  width: 100%;
  gap: 16px;
`;

/** "Selected Dates / Day Numbers" label + info icon; hovering either shows the dates tooltip. */
const SelectedDatesLabel = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
`;

/** Unified stat card: the label sits above a large number, both inside one bordered card. */
const Tile = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 4px;
  max-width: ${(props) => props.maxW || 'none'};
  @media screen and (max-width: 1023px) {
    max-width: ${(props) => props.maxWSmall || props.maxW || 'none'};
  }
  @media screen and (max-width: 767px) {
    max-width: none;
    min-width: 0;
  }
  border-radius: 4px;
  border: 1px solid #EEEEEE;
  background: ${(props) => (props.highlight ? '#D6D9DE' : '#FAFAFA')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  text-align: center;
`;

/** Number inside a capped stat card: renders at 16px, ellipsizes when it overflows the card, and
 * (via StatNumber) shows the full value in a tooltip only when actually truncated. */
const StatValue = styled('div')`
  position: relative;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-family: 'Nunito Sans';
  font-size: ${(props) => props.fontSize || '16px'};
  font-weight: 700;
  color: #333333;
  ${(props) => (props.fontSizeSmall
    ? `@media screen and (max-width: 390px) { font-size: ${props.fontSizeSmall}; }`
    : '')}
`;

/** Hidden width probe holding the full formatted value; font-family:inherit beats the global
 * `span { font-family: OpenSans }` rule so its width matches the real number. */
const Measure = styled('span')`
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  font-family: inherit;
`;

/** Stat number for the month cards: overflow is measured against the hidden full-value span, so the
 * count-up animation doesn't interfere. The tooltip attaches only when the number is truncated. */
const StatNumber = ({ value, fontSize = '16px', fontSizeSmall }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const full = (Number(value) || 0).toLocaleString('en-US');
  useEffect(() => {
    const c = containerRef.current;
    const m = measureRef.current;
    if (!c || !m) return undefined;
    const measure = () => setTruncated(m.offsetWidth > c.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Recompute once the card reaches its final width (and on sidebar/window resize) — a one-shot
    // measure can run before the layout settles and read the wrong width.
    const ro = new ResizeObserver(measure);
    ro.observe(c);
    return () => ro.disconnect();
  }, [full]);
  return (
    <StatValue
      ref={containerRef}
      fontSize={fontSize}
      fontSizeSmall={fontSizeSmall}
      data-tooltip-id='stats-tile-tip'
      data-tooltip-content={truncated ? full : undefined}
    >
      <AnimatedNumber value={value} countUpOnMount />
      <Measure ref={measureRef} aria-hidden>{full}</Measure>
    </StatValue>
  );
};

const StatsSideBarContent = () => {
  const [activeBreakdown, setActiveBreakdown] = useState('volume');
  // Week-view Tag Breakdown only: 'total' = whole-week aggregate, 'daily' = a single day.
  const [tagRangeMode, setTagRangeMode] = useState('total');
  // Stable key of the day selected in daily mode: date string (calendar) or dayNumber (program).
  const [dailyDayKey, setDailyDayKey] = useState(null);

  const programType = useSelector((state) => state.calendar.data.programType);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentlyDisplayedWeekdays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedWeekdays,
  );
  const currentlyDisplayedProgramDays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedProgramDays,
  );
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const monthViewWindowStart = useSelector(
    (state) => state.calendar.data.monthViewWindowStart,
  );

  const isProgram = programType === 'free-form-program';

  /** Month view shows a per-week breakdown for the full 10-week rolling window (derived from
   * monthViewWindowStart, the Sunday of the top week — same math as MonthView). No filters apply
   * here, so we sum calendarItems directly for each week's 7 days. The focus week (weeks[2]) is
   * highlighted to match the grid, and it all updates as infinite scroll shifts the window. */
  const monthWeeks = useMemo(() => {
    if (viewType !== 'monthView' || !monthViewWindowStart) return [];
    const start = moment(monthViewWindowStart, 'YYYY-MM-DD').startOf('week');
    const result = [];
    for (let w = 0; w < MONTH_VIEW_WEEKS; w += 1) {
      const weekStart = start.clone().add(w * 7, 'days');
      const weekEnd = weekStart.clone().add(6, 'days');
      let sets = 0;
      let reps = 0;
      let vol = 0;
      for (let d = 0; d < 7; d += 1) {
        const item = calendarItems[weekStart.clone().add(d, 'days').format('YYYY-MM-DD')];
        sets += item?.setCount || 0;
        reps += item?.repCount || 0;
        vol += item?.totalVolumeLoad || 0;
      }
      result.push({
        key: weekStart.format('YYYY-MM-DD'),
        label: `${weekStart.format('MMMM D')} - ${weekEnd.format('MMMM D')}`,
        isFocus: w === 2,
        sets,
        reps,
        vol,
      });
    }
    return result;
  }, [viewType, monthViewWindowStart, calendarItems]);

  /** Aggregate reps/sets/volume across the currently displayed days. Data lives per-day, so we
   * sum across the displayed set. Program totals are derived from the day's exercises rather than
   * read off the day — no program endpoint returns volume at all, and the reducer doesn't recompute
   * sets/reps on a cross-day move. Calendar days keep the stored values, which can be briefly
   * missing after a client-side edit until the silent refetch lands — hence the || 0. */
  const { totalSets, totalReps, totalVolume } = useMemo(() => {
    const acc = { totalSets: 0, totalReps: 0, totalVolume: 0 };
    if (isProgram) {
      currentlyDisplayedProgramDays.forEach(({ dayNumber }) => {
        const totals = getProgramDayTotals(nonDateBasedProgramItems[dayNumber]);
        acc.totalSets += totals.setCount;
        acc.totalReps += totals.repCount;
        acc.totalVolume += totals.totalVolumeLoad;
      });
    } else {
      currentlyDisplayedWeekdays.forEach(({ date }) => {
        const item = calendarItems[date];
        acc.totalSets += item?.setCount || 0;
        acc.totalReps += item?.repCount || 0;
        acc.totalVolume += item?.totalVolumeLoad || 0;
      });
    }
    return acc;
  }, [
    isProgram,
    currentlyDisplayedProgramDays,
    currentlyDisplayedWeekdays,
    calendarItems,
    nonDateBasedProgramItems,
  ]);

  /** Build the date-range label. When the displayed days are consecutive we show a range; otherwise
   * a comma-separated list (filters can make the displayed set non-sequential). */
  /** Returns { label, isList }. `isList` is true only for a filtered, non-sequential multi-day set
   * (e.g. "all Mondays") — the Volume date block collapses that to "Selected Dates" + a tooltip.
   * Contiguous ranges and single days keep their normal label with isList=false. */
  const dateBlockLabel = useMemo(() => {
    if (isProgram) {
      const days = [...currentlyDisplayedProgramDays].sort((a, b) => a.dayNumber - b.dayNumber);
      if (!days.length) return { label: '', isList: false };
      const label = (d) => `Week ${d.weekNumber} - Day ${d.dayInWeek}`;
      const nums = days.map((d) => d.dayNumber);
      const sequential = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
      if (sequential && days.length > 1) {
        return { label: `${label(days[0])} → ${label(days[days.length - 1])}`, isList: false };
      }
      return { label: days.map(label).join(', '), isList: days.length > 1 };
    }
    const dates = [...currentlyDisplayedWeekdays].map((d) => d.date).sort();
    if (!dates.length) return { label: '', isList: false };
    const fmt = (d) => moment(d).format('MMM D');
    const sequential = dates.every(
      (d, i) => i === 0 || moment(d).diff(moment(dates[i - 1]), 'days') === 1,
    );
    if (sequential && dates.length > 1) {
      return { label: `${fmt(dates[0])} - ${fmt(dates[dates.length - 1])}`, isList: false };
    }
    return { label: dates.map(fmt).join(', '), isList: dates.length > 1 };
  }, [isProgram, currentlyDisplayedProgramDays, currentlyDisplayedWeekdays]);

  /** Day view shows the single displayed day; the label reads that day directly
   * (currentlyDisplayed* is only set in week view). Program mode uses stored week/day nums. */
  const dayViewLabel = useMemo(() => {
    if (isProgram) {
      const item = nonDateBasedProgramItems[selectedProgramDayNumber];
      if (item && item.weekNumber != null && item.dayInWeek != null) {
        return `Week ${item.weekNumber} - Day ${item.dayInWeek}`;
      }
      return selectedProgramDayNumber != null ? `Day ${selectedProgramDayNumber}` : '';
    }
    return selectedDate ? moment(selectedDate).format('dddd MMMM Do') : '';
  }, [isProgram, nonDateBasedProgramItems, selectedProgramDayNumber, selectedDate]);

  /** Month view aggregates the full 10-week rolling window, so the tag date box spans that whole
   * range (not a single week). Same day set as monthWeeks / the tag table's month aggregation. */
  const monthRangeLabel = useMemo(() => {
    if (viewType !== 'monthView' || !monthViewWindowStart) return '';
    const start = moment(monthViewWindowStart, 'YYYY-MM-DD').startOf('week');
    const end = start.clone().add(MONTH_VIEW_WINDOW_DAYS - 1, 'days');
    return `${start.format('MMM D')} - ${end.format('MMM D')}`;
  }, [viewType, monthViewWindowStart]);

  /** The days of the currently displayed week, as { key, label }, for the Daily-mode day nav. The
   * key is the same value the tag table looks up by (raw date string for calendar, dayNumber for
   * program), so navigating it maps straight onto calendarItems / nonDateBasedProgramItems. */
  const weekDays = useMemo(() => {
    if (isProgram) {
      return [...currentlyDisplayedProgramDays]
        .sort((a, b) => a.dayNumber - b.dayNumber)
        .map((d) => ({
          key: d.dayNumber,
          label: d.weekNumber != null && d.dayInWeek != null
            ? `Week ${d.weekNumber} - Day ${d.dayInWeek}`
            : `Day ${d.dayNumber}`,
        }));
    }
    return [...currentlyDisplayedWeekdays]
      .sort((a, b) => moment(a.date).diff(moment(b.date)))
      .map((d) => ({ key: d.date, label: moment(d.date).format('dddd MMMM Do') }));
  }, [isProgram, currentlyDisplayedProgramDays, currentlyDisplayedWeekdays]);

  /** Keep dailyDayKey valid: when it isn't in the current week (mount, week navigation, filter
   * change) default to the first displayed day of the week. */
  useEffect(() => {
    if (!weekDays.length) return;
    if (weekDays.some((d) => d.key === dailyDayKey)) return;
    setDailyDayKey(weekDays[0].key);
  }, [weekDays, dailyDayKey]);

  const dailyIndex = weekDays.findIndex((d) => d.key === dailyDayKey);
  const dailyLabel = dailyIndex >= 0 ? weekDays[dailyIndex].label : '';

  const stepDailyDay = (delta) => {
    const next = dailyIndex + delta;
    if (next < 0 || next >= weekDays.length) return;
    setDailyDayKey(weekDays[next].key);
  };

  /** Day view supports only the Tag Breakdown (no Volume tab). In week/month view the user can
   * toggle between Volume and Tag breakdowns. Volume shows the single aggregated block in week view
   * and the per-week 10-week breakdown in month view. */
  const isDayView = viewType === 'dayView';
  const showTagBody = isDayView || activeBreakdown === 'tag';
  const isVolume = !isDayView && activeBreakdown === 'volume';
  const showWeekBody = isVolume && viewType !== 'monthView';
  const showMonthBody = isVolume && viewType === 'monthView';

  /** The Daily/Total toggle is week-view Tag Breakdown only (calendar + free-form program). */
  const showTagRangeToggle = viewType === 'weekView' && showTagBody;
  const isDailyMode = showTagRangeToggle && tagRangeMode === 'daily';
  let selectedTagDay = null;
  if (isDailyMode && dailyIndex >= 0) {
    selectedTagDay = isProgram ? { dayNumber: dailyDayKey } : { date: dailyDayKey };
  }

  return (
    <Wrapper>
      {isDayView ? (
        <TabBar>
          <TabItem isSelected style={{ cursor: 'default' }}>
            <Text fontSize='15px' fontWeight={700} color='#444444'>Tag Breakdown</Text>
          </TabItem>
        </TabBar>
      ) : (
        <TabBar>
          <TabItem
            isSelected={activeBreakdown === 'volume'}
            onClick={() => setActiveBreakdown('volume')}
          >
            <Text
              fontSize='15px'
              fontWeight={activeBreakdown === 'volume' ? 700 : 400}
              color={activeBreakdown === 'volume' ? '#444444' : '#9E9E9E'}
            >
              Volume Breakdown
            </Text>
          </TabItem>
          <TabItem
            isSelected={activeBreakdown === 'tag'}
            onClick={() => setActiveBreakdown('tag')}
          >
            <Text
              fontSize='15px'
              fontWeight={activeBreakdown === 'tag' ? 700 : 400}
              color={activeBreakdown === 'tag' ? '#444444' : '#9E9E9E'}
            >
              Tag Breakdown
            </Text>
          </TabItem>
        </TabBar>
      )}

      {/* Week-view Tag Breakdown: toggle between the whole-week aggregate and a single day. */}
      {showTagRangeToggle && (
        <SegmentedControl>
          <Segment
            isSelected={tagRangeMode === 'daily'}
            onClick={() => {
              setTagRangeMode('daily');
              setDailyDayKey(weekDays.length ? weekDays[0].key : null);
            }}
          >
            <Text
              fontSize='13px'
              fontWeight={tagRangeMode === 'daily' ? 700 : 600}
              color={tagRangeMode === 'daily' ? '#444444' : '#9E9E9E'}
            >
              Day
            </Text>
          </Segment>
          <Segment
            isSelected={tagRangeMode === 'total'}
            onClick={() => setTagRangeMode('total')}
          >
            <Text
              fontSize='13px'
              fontWeight={tagRangeMode === 'total' ? 700 : 600}
              color={tagRangeMode === 'total' ? '#444444' : '#9E9E9E'}
            >
              Week
            </Text>
          </Segment>
        </SegmentedControl>
      )}

      {/* Tag view: the date box stays fixed above the scrolling table (outside ScrollBody). In
          Daily mode it becomes a back/forward nav across the displayed week's days. */}
      {showTagBody && (
        isDailyMode ? (
          <DateBlock isNav borderless padTop>
            <NavArrow
              disabled={dailyIndex <= 0}
              onClick={() => stepDailyDay(-1)}
            >
              <IcomoonReact iconSet={iconSet} size={16} icon='left-arrow' color='#444444' />
            </NavArrow>
            <Text fontSize='16px' fontWeight={600} color='#444444'>{dailyLabel}</Text>
            <NavArrow
              disabled={dailyIndex >= weekDays.length - 1}
              onClick={() => stepDailyDay(1)}
            >
              <IcomoonReact iconSet={iconSet} size={16} icon='right-arrow' color='#444444' />
            </NavArrow>
          </DateBlock>
        ) : (
          <DateBlock borderless padTop>
            <Text fontSize='16px' fontWeight={600} color='#444444'>
              {isDayView && dayViewLabel}
              {!isDayView && viewType === 'monthView' && monthRangeLabel}
              {!isDayView && viewType !== 'monthView' && dateBlockLabel.label}
            </Text>
          </DateBlock>
        )
      )}

      <ScrollBody>
        {showTagBody && <StatsTagBreakdown selectedTagDay={selectedTagDay} />}

        {showMonthBody && (
        <>
          {monthWeeks.map((week) => (
            <WeekSummary key={week.key}>
              <Text fontSize='16px' fontWeight={600} color='#444444' textAlign='center'>
                {week.label}
              </Text>
              <WeekStatsRow>
                <Tile maxW='112px' maxWSmall='84px' highlight={week.isFocus}>
                  <Text fontSize='12px' fontWeight={700} color='#444444'>Total Sets</Text>
                  <StatNumber value={week.sets} />
                </Tile>
                <Tile maxW='112px' maxWSmall='84px' highlight={week.isFocus}>
                  <Text fontSize='12px' fontWeight={700} color='#444444'>Total Reps</Text>
                  <StatNumber value={week.reps} />
                </Tile>
                <Tile maxW='112px' maxWSmall='84px' highlight={week.isFocus}>
                  <Text fontSize='12px' fontWeight={700} color='#444444'>Estimated Volume</Text>
                  <StatNumber value={week.vol} />
                </Tile>
              </WeekStatsRow>
            </WeekSummary>
          ))}
        </>
        )}

        {showWeekBody && (
        <>
          {dateBlockLabel.isList ? (
            <SelectedDatesLabel
              data-tooltip-id='stats-tile-tip'
              data-tooltip-content={dateBlockLabel.label}
            >
              <Text fontSize='16px' fontWeight={600} color='#444444'>
                {isProgram ? 'Selected Day Numbers' : 'Selected Dates'}
              </Text>
              <IcomoonReact iconSet={iconSet} size={16} icon='info2' color='#9E9E9E' />
            </SelectedDatesLabel>
          ) : (
            <Text fontSize='16px' fontWeight={600} color='#444444' textAlign='center'>
              {dateBlockLabel.label}
            </Text>
          )}

          <TileRow>
            <Tile maxW='168px' maxWSmall='126px'>
              <Text fontSize='12px' fontWeight={700} color='#444444'>Total Sets</Text>
              <StatNumber value={totalSets} fontSize='36px' fontSizeSmall='24px' />
            </Tile>
            <Tile maxW='168px' maxWSmall='126px'>
              <Text fontSize='12px' fontWeight={700} color='#444444'>Total Reps</Text>
              <StatNumber value={totalReps} fontSize='36px' fontSizeSmall='24px' />
            </Tile>
          </TileRow>

          <Text fontSize='11px' color='#9E9E9E'>
            Rep count is incremented when&nbsp;
            <Text as='span' fontSize='11px' fontWeight={700} color='#616161'>Track Rep Count</Text>
            &nbsp;is enabled for a scheduled exercise. In the case of&nbsp;
            <Text as='span' fontSize='11px' fontWeight={700} color='#616161'>Rep Ranges</Text>
            &nbsp;the lowest value is used.
          </Text>

          <TileRow>
            <Tile maxW='352px' maxWSmall='268px'>
              <Text fontSize='12px' fontWeight={700} color='#444444'>Estimated Volume</Text>
              <StatNumber value={totalVolume} fontSize='36px' fontSizeSmall='24px' />
            </Tile>
          </TileRow>

          {/* Percentage-based loads resolve against an athlete's max, and a program has no
              athlete — so they never contribute here and the clause is omitted for programs. */}
          <Text fontSize='11px' color='#9E9E9E'>
            Estimated load is incremented when&nbsp;
            <Text as='span' fontSize='11px' fontWeight={700} color='#616161'>Track Volume Load</Text>
            {isProgram ? <>&nbsp;is enabled and&nbsp;</> : <>&nbsp;is enabled and either&nbsp;</>}
            <Text as='span' fontSize='11px' fontWeight={700} color='#616161'>Prescribed Load</Text>
            {!isProgram && (
              <>
                &nbsp;or&nbsp;
                <Text as='span' fontSize='11px' fontWeight={700} color='#616161'>Percentage</Text>
              </>
            )}
            &nbsp;is set for a scheduled exercise.
          </Text>
        </>
        )}
      </ScrollBody>
      {/* Shared overflow tooltip for the stat numbers (week + month). Styled to match the exercise-
          name tooltip. Portaled to <body> so it escapes the sidebar's stacking context and paints
          over the left icon sidebar; positionStrategy fixed keeps it anchored to the number. */}
      {typeof document !== 'undefined' && createPortal(
        <ReactTooltip
          id='stats-tile-tip'
          variant='light'
          positionStrategy='fixed'
          opacity={1}
          style={{
            background: '#FFFFFF',
            color: '#444444',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            fontWeight: 400,
            borderRadius: '4px',
            padding: '8px',
            boxShadow: '0px 0px 1px rgb(48 49 51 / 5%), 0px 4px 8px rgb(48 49 51 / 10%)',
            maxWidth: '200px',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            zIndex: 9999,
          }}
        />,
        document.body,
      )}
    </Wrapper>
  );
};

export default StatsSideBarContent;
